// Cliente fetch das functions do painel Company.

export interface UserSession {
  id: string;
  email: string;
  name: string;
}
export interface ActiveContext {
  kind: "platform" | "company";
  companyId?: string;
  companyName?: string;
  role: string;
}
export interface UserProfile {
  kind: "platform" | "company";
  role: string;
  companies: Array<{ id: string; name: string }>;
}
export interface MeResponse {
  user: UserSession;
  activeContext: ActiveContext | null;
  contexts: ActiveContext[];
  profiles: UserProfile[];
}

export interface CompanyHome {
  kpis: {
    teachers: number;
    students: number;
    admins: number;
    disciplinas: { draft: number; active: number; closed: number };
    turmas: number;
    offerings: number;
  };
  upcomingExams: Array<{
    id: string;
    title: string;
    startsAt: string | null;
    endsAt: string | null;
    status: string;
    disciplinaName: string;
  }>;
}

export interface TeacherRow {
  userId: string;
  email: string;
  name: string;
  apelido: string | null;
  grantedAt: string;
  suspended: boolean;
  disciplinaCount: number;
  bankCount: number;
  groupCount: number;
}

export interface DisciplinaRow {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  centroCusto: string | null;
  semesterId: string | null;
  startsAt: string | null;
  endsAt: string | null;
  gradingEnabled: boolean;
  status: "draft" | "active" | "closed";
  createdAt: string;
  updatedAt: string;
  offeringCount: number;
  teacherCount: number;
}

export interface DisciplinaDetail extends DisciplinaRow {
  semester: { id: string; label: string } | null;
  teachers: Array<{ userId: string; email: string; name: string; apelido: string | null }>;
  offerings: Array<{
    id: string;
    turmaId: string;
    turmaName: string;
    turmaCode: string | null;
    createdAt: string;
  }>;
}

export interface TurmaRow {
  id: string;
  name: string;
  code: string | null;
  archivedAt: string | null;
  createdAt: string;
  enrolledCount: number;
}

export interface TurmaDetail {
  id: string;
  name: string;
  code: string | null;
  archivedAt: string | null;
  createdAt: string;
  enrollments: Array<{
    userId: string;
    email: string;
    name: string;
    apelido: string | null;
    enrolledAt: string;
    withdrawnAt: string | null;
  }>;
  metadata: Array<{
    fieldId: string;
    value: string | null;
    key: string;
    label: string;
    type: string;
  }>;
}

export interface StudentRow {
  userId: string;
  email: string;
  name: string;
  apelido: string | null;
  grantedAt: string;
  suspended: boolean;
  turmasAtivas: number;
}

export interface StudentDetail {
  student: {
    id: string;
    email: string;
    name: string;
    apelido: string | null;
    suspended: boolean;
    createdAt: string;
  };
  enrollments: Array<any>;
  disciplinas: Array<{
    id: string;
    name: string;
    code: string | null;
    status: string;
    turmaId: string;
    offeringId: string | null;
    saldo: number;
  }>;
  provas: Array<any>;
  compras: Array<{
    id: string;
    offeringId: string;
    disciplinaName: string;
    turmaName: string;
    pacoteNomeSnap: string;
    numAulasSnap: number;
    valorCents: number;
    status: "pending" | "paid" | "expired" | "canceled";
    txid: string;
    pixBrcode: string;
    createdAt: string;
    paidAt: string | null;
    expiresAt: string | null;
    canceledAt: string | null;
  }>;
  aulas: Array<{
    id: string;
    offeringId: string;
    disciplinaName: string;
    turmaName: string;
    professorUserId: string;
    dataHora: string;
    duracaoMin: number;
    status: "scheduled" | "realized" | "canceled" | "no_show";
    creditosConsumidos: number;
    descricao: string | null;
    canceledReason: string | null;
    realizedAt: string | null;
  }>;
}

export interface MetadataField {
  id: string;
  companyId: string;
  key: string;
  label: string;
  type: "text" | "number" | "date" | "select";
  options: string[] | null;
}

export interface AuditLog {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  at: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {}
  if (!res.ok) {
    const err = body as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      res.status,
      err?.error?.code ?? "http_error",
      err?.error?.message ?? `HTTP ${res.status}`
    );
  }
  return body as T;
}

export const api = {
  me: () => call<MeResponse>("/api/auth/me"),
  logout: () => call<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  switchContext: (ctx: { kind: "platform" | "company"; role: string; companyId?: string }) =>
    call<{ ok: true }>("/api/auth/switch-context", {
      method: "POST",
      body: JSON.stringify(ctx)
    }),
  home: () => call<CompanyHome>("/api/company/home"),
  // Professors
  listProfessors: () => call<{ teachers: TeacherRow[] }>("/api/company/professors"),
  addProfessor: (userIdOrEmail: string) =>
    call<{ ok: true }>("/api/company/professors", {
      method: "POST",
      body: JSON.stringify(
        userIdOrEmail.includes("@")
          ? { email: userIdOrEmail }
          : { userId: userIdOrEmail }
      )
    }),
  removeProfessor: (userId: string) =>
    call<{ ok: true }>(`/api/company/professors?userId=${encodeURIComponent(userId)}`, {
      method: "DELETE"
    }),
  // Disciplinas
  listDisciplinas: () => call<{ disciplinas: DisciplinaRow[] }>("/api/company/disciplinas"),
  getDisciplina: (id: string) =>
    call<{ disciplina: DisciplinaDetail }>(`/api/company/disciplinas?id=${encodeURIComponent(id)}`),
  createDisciplina: (data: any) =>
    call<{ disciplina: DisciplinaDetail }>("/api/company/disciplinas", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchDisciplina: (id: string, data: any) =>
    call<{ disciplina: DisciplinaDetail }>(`/api/company/disciplinas?id=${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  addOffering: (disciplinaId: string, turmaId: string) =>
    call<{ disciplina: DisciplinaDetail }>(
      `/api/company/disciplinas?id=${encodeURIComponent(disciplinaId)}&action=offering`,
      { method: "POST", body: JSON.stringify({ turmaId }) }
    ),
  removeOffering: (disciplinaId: string, offeringId: string) =>
    call<{ disciplina: DisciplinaDetail }>(
      `/api/company/disciplinas?id=${encodeURIComponent(disciplinaId)}&offeringId=${encodeURIComponent(offeringId)}`,
      { method: "DELETE" }
    ),
  addDisciplinaTeacher: (disciplinaId: string, userId: string) =>
    call<{ disciplina: DisciplinaDetail }>(
      `/api/company/disciplinas?id=${encodeURIComponent(disciplinaId)}&action=teacher`,
      { method: "POST", body: JSON.stringify({ userId }) }
    ),
  removeDisciplinaTeacher: (disciplinaId: string, teacherUserId: string) =>
    call<{ disciplina: DisciplinaDetail }>(
      `/api/company/disciplinas?id=${encodeURIComponent(disciplinaId)}&teacherUserId=${encodeURIComponent(teacherUserId)}`,
      { method: "DELETE" }
    ),
  closeDisciplina: (id: string) =>
    call<{ closed?: string; deleted?: string }>(
      `/api/company/disciplinas?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // Turmas
  listTurmas: () => call<{ turmas: TurmaRow[] }>("/api/company/turmas"),
  getTurma: (id: string) =>
    call<{ turma: TurmaDetail }>(`/api/company/turmas?id=${encodeURIComponent(id)}`),
  createTurma: (data: any) =>
    call<{ turma: TurmaDetail }>("/api/company/turmas", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchTurma: (id: string, data: any) =>
    call<{ turma: TurmaDetail }>(`/api/company/turmas?id=${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  enrollStudent: (turmaId: string, userIdOrEmail: string) =>
    call<{ turma: TurmaDetail }>(
      `/api/company/turmas?id=${encodeURIComponent(turmaId)}&action=enroll`,
      {
        method: "POST",
        // Detecta heurística email vs uuid pelo "@". Email é o caminho preferido
        // pra o operador; userId raw fica como fallback (ex.: vindo de picker).
        body: JSON.stringify(
          userIdOrEmail.includes("@")
            ? { email: userIdOrEmail }
            : { userId: userIdOrEmail }
        )
      }
    ),
  unenrollStudent: (turmaId: string, studentUserId: string) =>
    call<{ turma: TurmaDetail }>(
      `/api/company/turmas?id=${encodeURIComponent(turmaId)}&studentUserId=${encodeURIComponent(studentUserId)}`,
      { method: "DELETE" }
    ),
  archiveTurma: (id: string) =>
    call<{ archived: string }>(`/api/company/turmas?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  // Students
  listStudents: () => call<{ students: StudentRow[] }>("/api/company/students"),
  getStudent: (userId: string) =>
    call<StudentDetail>(`/api/company/students?userId=${encodeURIComponent(userId)}`),
  // Settings
  getSettings: () =>
    call<{
      company: {
        id: string;
        slug: string;
        name: string;
        isAutonomous: boolean;
        settings: Record<string, unknown>;
      };
    }>("/api/company/settings"),
  saveSettings: (settings: Record<string, unknown>) =>
    call<{ ok: true }>("/api/company/settings", {
      method: "PUT",
      body: JSON.stringify({ settings })
    }),
  // Metadata fields
  listMetadataFields: () => call<{ fields: MetadataField[] }>("/api/company/metadata-fields"),
  createMetadataField: (data: any) =>
    call<{ field: MetadataField }>("/api/company/metadata-fields", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchMetadataField: (id: string, data: any) =>
    call<{ field: MetadataField }>(
      `/api/company/metadata-fields?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteMetadataField: (id: string) =>
    call<{ deleted: string }>(`/api/company/metadata-fields?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  // Audit
  listAudit: (params: { q?: string; entityType?: string } = {}) => {
    const sp = new URLSearchParams();
    if (params.q) sp.set("q", params.q);
    if (params.entityType) sp.set("entityType", params.entityType);
    return call<{ logs: AuditLog[] }>(`/api/company/audit${sp.size ? "?" + sp : ""}`);
  },
  listPagamentos: (status: "pending" | "paid" | "all" = "pending") =>
    call<{ compras: CompraAdminRow[] }>(
      `/api/company/pagamentos?status=${encodeURIComponent(status)}`
    ),
  confirmarPagamento: (id: string, e2eId?: string, notify = true) =>
    call<{ compra: any }>(
      `/api/company/pagamentos?id=${encodeURIComponent(id)}&action=confirm-manual`,
      { method: "POST", body: JSON.stringify({ e2eId, notify }) }
    ),
  cancelarPagamento: (id: string, reason?: string) =>
    call<{ canceled: string }>(
      `/api/company/pagamentos?id=${encodeURIComponent(id)}&action=cancel`,
      { method: "POST", body: JSON.stringify({ reason }) }
    ),
  /** Gera (ou retorna) o link público de pagamento pra uma compra. */
  getChargeLink: (compraId: string) =>
    call<{ slug: string; url: string }>(
      `/api/charges/link?compraId=${encodeURIComponent(compraId)}`,
      { method: "POST" }
    ),
  /** KPIs + inadimplentes + por centro de custo (mês corrente). */
  pagamentosSummary: () =>
    call<{
      kpis: {
        receitaMesCents: number;
        receitaMesCount: number;
        pendenteCents: number;
        pendenteCount: number;
        inadimplenteCount: number;
      };
      inadimplentes: Array<{
        id: string;
        alunoUserId: string;
        alunoName: string;
        alunoEmail: string;
        pacoteNomeSnap: string;
        valorCents: number;
        createdAt: string;
        diasAtraso: number;
        disciplinaName: string;
        centroCusto: string | null;
      }>;
      porCentroCusto: Array<{
        centroCusto: string;
        receitaCents: number;
        count: number;
      }>;
    }>("/api/company/pagamentos-summary"),
  // ===== Notificações =====
  listNotifications: (opts: { onlyUnread?: boolean; includeHidden?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.onlyUnread) q.set("onlyUnread", "1");
    if (opts.includeHidden) q.set("include", "hidden");
    const qs = q.toString();
    return call<{ notifications: NotificationRow[]; unreadCount: number }>(
      `/api/notifications${qs ? "?" + qs : ""}`
    );
  },
  markNotifRead: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=read`,
      { method: "PATCH" }
    ),
  markNotifUnread: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=unread`,
      { method: "PATCH" }
    ),
  hideNotif: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=hide`,
      { method: "PATCH" }
    ),
  markAllNotifsRead: () =>
    call<{ ok: true }>(`/api/notifications?action=read-all`, {
      method: "PATCH"
    }),
  // ===== Webhooks =====
  listWebhooks: () =>
    call<{
      subscriptions: WebhookSubscription[];
      events: WebhookEventLog[];
    }>("/api/company/webhooks"),
  createWebhook: (data: { url: string; events?: string[]; description?: string }) =>
    call<{ subscription: WebhookSubscription; secret: string }>(
      "/api/company/webhooks",
      { method: "POST", body: JSON.stringify(data) }
    ),
  rotateWebhookSecret: (id: string) =>
    call<{ secret: string }>(
      `/api/company/webhooks?id=${encodeURIComponent(id)}&action=rotate-secret`,
      { method: "POST" }
    ),
  patchWebhook: (
    id: string,
    data: { url?: string; events?: string[]; active?: boolean; description?: string }
  ) =>
    call<{ subscription: WebhookSubscription }>(
      `/api/company/webhooks?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteWebhook: (id: string) =>
    call<{ ok: true }>(`/api/company/webhooks?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  runWebhookWorker: () =>
    call<{
      ok: true;
      picked: number;
      delivered: number;
      retried: number;
      failed: number;
    }>("/api/webhooks/worker", { method: "POST" }),
  listSubscriptions: (params?: {
    status?: "active" | "past_due" | "paused" | "canceled";
    billingType?: "pix_monthly" | "credit_card";
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.billingType) q.set("billingType", params.billingType);
    const qs = q.toString();
    return call<{ items: SubscriptionAdminRow[]; summary: SubscriptionSummary }>(
      `/api/company/subscriptions${qs ? "?" + qs : ""}`
    );
  }
};

export interface SubscriptionChargeRow {
  id: string;
  status: "pending" | "succeeded" | "failed" | "refunded";
  provider: "pix_manual" | "stripe" | "mercadopago" | "mock";
  valorCents: number;
  failureReason: string | null;
  methodPriority: number | null;
  chargedAt: string;
}

export interface SubscriptionAdminRow {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  pacoteNomeSnap: string;
  valorCents: number;
  billingType: "pix_monthly" | "credit_card";
  status: "active" | "paused" | "past_due" | "canceled";
  nextChargeAt: string;
  lastChargeAt: string | null;
  failedAttempts: number;
  createdAt: string;
  recentCharges: SubscriptionChargeRow[];
}

export interface SubscriptionSummary {
  active: number;
  pastDue: number;
  paused: number;
  totalMonthly: number;
  byType: {
    pix_monthly: number;
    credit_card: number;
  };
}

export interface WebhookSubscription {
  id: string;
  companyId: string;
  url: string;
  secretPreview?: string;
  events: string[];
  active: boolean;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookEventLog {
  id: string;
  subscriptionId: string;
  kind: string;
  status: "pending" | "delivered" | "failed";
  attempts: number;
  lastStatusCode: number | null;
  lastResponseMs: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
  lastAttemptAt: string | null;
}

// ===== Notificações =====
export type NotificationKind =
  | "payment_received"
  | "payment_pending"
  | "lesson_scheduled"
  | "lesson_logged"
  | "lesson_canceled"
  | "lesson_reminder"
  | "credit_low"
  | "credit_zero"
  | "enrolled_in_offering"
  | "assigned_to_disciplina"
  | "system";

export interface NotificationRow {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  payload: Record<string, unknown> | null;
  linkUrl: string | null;
  readAt: string | null;
  hiddenAt: string | null;
  createdAt: string;
}

export interface CompraAdminRow {
  id: string;
  offeringId: string;
  alunoUserId: string;
  alunoEmail: string;
  alunoName: string;
  pacoteNomeSnap: string;
  numAulasSnap: number;
  valorCents: number;
  txid: string;
  e2eId: string | null;
  pixBrcode: string;
  status: "pending" | "paid" | "expired" | "canceled";
  createdAt: string;
  paidAt: string | null;
  disciplinaId: string;
  disciplinaName: string;
}
